import { Component, Input, Output, EventEmitter, Injector } from "@angular/core";
import { AuthService } from "../../service/auth-service/auth-service.service";
import { Art57Section } from "../../ui/Art57Section/Art57Section";
import { TreeNodeATCCodeList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeATCCodeList";
import { TreeNodeAttachmentList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeAttachmentList";
import { TreeNodeAuthorisedPharmaceuticalFormList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeAuthorisedPharmaceuticalFormList";
import { TreeNodeDrugIngredientsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeDrugIngredientsList";
import { TreeNodeDrugRouteList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeDrugRouteList";
import { TreeNodeDrugsATCList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeDrugsATCList";
import { TreeNodeDrugsIndicationsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeDrugsIndicationsList";
import { TreeNodeMasterFileLocationList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeMasterFileLocationList";
import { TreeNodeMedicalDeviceList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeMedicalDeviceList";
import { TreeNodeMedicinalProductTypeList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeMedicinalProductTypeList";
import { TreeNodeOrganisationList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeOrganisationList";
import { TreeNodePharmaceuticalFormList } from "../../ui/Node/Art57Node/ListEntity/TreeNodePharmaceuticalFormList";
import { TreeNodePharmaceuticalProductsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodePharmaceuticalProductsList";
import { TreeNodePreviousEVCodeList } from "../../ui/Node/Art57Node/ListEntity/TreeNodePreviousEVCodeList";
import { TreeNodeProductAttachmentList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeProductAttachmentList";
import { TreeNodeProductsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeProductsList";
import { TreeNodeRoutesOfAdministrationList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeRoutesOfAdministrationList";
import { TreeNodeSourceList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSourceList";
import { TreeNodeSubstanceAliasList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstanceAliasList";
import { TreeNodeSubstanceAliasTranslationsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstanceAliasTranslationsList";
import { TreeNodeSubstanceAttachmentFieldsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstanceAttachmentFieldsList";
import { TreeNodeSubstanceInternationalCodeList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstanceInternationalCodeList";
import { TreeNodeSubstanceParentCodeFieldsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstanceParentCodeFieldsList";
import { TreeNodeSubstancePreviousEvCodeFieldsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstancePreviousEvCodeFieldsList";
import { TreeNodeSubstancePreviousNameCodeFieldsList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstancePreviousNameCodeFieldsList";
import { TreeNodeSubstancesList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstancesList";
import { TreeNodeSubstanceTranslationList } from "../../ui/Node/Art57Node/ListEntity/TreeNodeSubstanceTranslationList";
import { environment } from "../../../../environments/environment";


@Component({
  selector: 'app-list-detailed-add-nodes',
  templateUrl: './list-detailed-add-nodes.component.html',
  styleUrl: './list-detailed-add-nodes.component.scss'
})
export class ListDetailedAddNodesComponent {
  @Input() selectedNode: any;
  @Output() buttonAction = new EventEmitter();

  constructor ( public authService: AuthService,
                private injector: Injector
              ) {

  }
  
  onButtonAction(buttonAction: String, parameter: string = '') {
    const data = { action: buttonAction, param: parameter };
    
    if (this.hasPermissionsToCreateNode(data)) {
      this.buttonAction.emit(data);
    }
  };

  hasPermissionsToCreateNode(data: { action: String; param: string }): boolean {
    let isAuthorised = false;

    if (this.isProductsList()
      || this.isATCsList()
      || this.isPharmaceuticalFormsList()
      || this.isRoutesOfAdministrationsList()) {
      
      let nodeTypeValue: number | null = this.getNodeType(data?.param);
      if (nodeTypeValue == null) {
        this.handleWarning('Cannot verify the permissions for this action.');
      }
      else {
        switch (true) {
          case this.isProductsList() && nodeTypeValue === 1 && !this.hasRole('EVH_DEVELOPMENT_PRODUCT_INSERT'):
            this.handleWarning('The Command "Creation of a New Development Product" cannot be executed. Organisation is not allowed to send Development Products.', 5000);
            break;
          case this.isProductsList() && nodeTypeValue === 2 && !this.hasRole('EVH_AUTHORISED_PRODUCT_INSERT'):
            this.handleWarning('The Command "Creation of a New Authorised Product" cannot be executed. Organisation is not allowed to send Authorised Products.', 5000);
            break;
          case this.isATCsList() && nodeTypeValue === 1 && !this.hasRole('EVH_DEVELOPMENT_ATC_INSERT'):
            this.handleWarning('The Command "Creation of a New Development ATC Code" cannot be executed. Organisation is not allowed to send Development ATC Codes.', 5000);
            break;
          case this.isPharmaceuticalFormsList() && nodeTypeValue === 1 && !this.hasRole('EVH_DEVELOPMENT_PF_INSERT'):
            this.handleWarning('The Command "Creation of a New Development Pharmaceutical Form" cannot be executed. Organisation is not allowed to send Development Pharmaceutical Forms.', 5000);
            break;
          case this.isRoutesOfAdministrationsList() && nodeTypeValue === 1 && !this.hasRole('EVH_DEVELOPMENT_ROA_INSERT'):
            this.handleWarning('The Command "Creation of a New Development Route of Administration" cannot be executed. Organisation is not allowed to send Development Administration Routes.', 5000);
            break;
          default:
            isAuthorised = true;
            break;
        }
      }
    }
    else {
      isAuthorised = true;
    }

    return isAuthorised;
  }

  private getNodeType(param: string | undefined): number | null {
    try {
      const parsedParam = param ? JSON.parse(JSON.stringify(param)) : null;
      return parsedParam?.nodeType ?? null;
    } catch (error) {
      this.handleError("Error found. Not possible to parse the node type.");
      return null;
    }
  }

  isProductsList(){
    return this.selectedNode instanceof TreeNodeProductsList;
  }

  isSubstancesList(){
    return this.selectedNode instanceof TreeNodeSubstancesList;
  }

  isSourcesList() {
    return this.selectedNode instanceof TreeNodeSourceList;
  }

  isOrganisationsList() {
    return this.selectedNode instanceof TreeNodeOrganisationList;
  }

  isATCsList(){
    return this.selectedNode instanceof TreeNodeATCCodeList;
  }

  isPharmaceuticalFormsList(){
    return this.selectedNode instanceof TreeNodePharmaceuticalFormList
  }

  isRoutesOfAdministrationsList(){
    return this.selectedNode instanceof TreeNodeRoutesOfAdministrationList
  }

  isAttachmentsList(){
    return this.selectedNode instanceof TreeNodeAttachmentList
  }

  isMasterFileLocationsList(){
    return this.selectedNode instanceof TreeNodeMasterFileLocationList
  }

  isMedicinalProductTypesList(){
    return this.selectedNode instanceof TreeNodeMedicinalProductTypeList
  }

  isAuthorisedPharmaceuticalFormList(){
    return this.selectedNode instanceof TreeNodeAuthorisedPharmaceuticalFormList
  }

  isPharmaceuticalProductsList() {
    return this.selectedNode instanceof TreeNodePharmaceuticalProductsList
  }

  isDrugRouteList(){
    return this.selectedNode instanceof TreeNodeDrugRouteList
  }

  isDrugIngredientsList(){
    return this.selectedNode instanceof TreeNodeDrugIngredientsList
  }

  isMedicalDevicesList(){
    return this.selectedNode instanceof TreeNodeMedicalDeviceList
  }

  isDrugsATCList(){
    return this.selectedNode instanceof TreeNodeDrugsATCList
  }

  isDrugIndicationList(){
    return this.selectedNode instanceof TreeNodeDrugsIndicationsList
  }

  isPreviousEVCodeList(){
    return this.selectedNode instanceof TreeNodePreviousEVCodeList
  }

  isProductAttachmentsList(){
    return this.selectedNode instanceof TreeNodeProductAttachmentList
  }

  isSubstanceTranslationsList(){
    return this.selectedNode instanceof TreeNodeSubstanceTranslationList
  }

  isSubstanceAliasList(){
    return this.selectedNode instanceof TreeNodeSubstanceAliasList
  }

  isSubstanceAliasTranslationList() {
    return this.selectedNode instanceof TreeNodeSubstanceAliasTranslationsList
  }

  isSubstanceInternationalCodeList(){
    return this.selectedNode instanceof TreeNodeSubstanceInternationalCodeList
  }

  isSubstanceParentCodeList(){
    return this.selectedNode instanceof TreeNodeSubstanceParentCodeFieldsList
  }

  isSubstancePreviousEVCodeList () {
    return this.selectedNode instanceof TreeNodeSubstancePreviousEvCodeFieldsList
  }
  
  isSubstanceAttachmentsList () {
    return this.selectedNode instanceof TreeNodeSubstanceAttachmentFieldsList
  }
  
  isSubstancePreviousNameCodeList(){
    return this.selectedNode instanceof TreeNodeSubstancePreviousNameCodeFieldsList
  }

  isCreateAndSendProductSection() {
    return this.injector.get(Art57Section).isCreateAndSendProductSection(); 
  }

  hasRole(role: string): boolean {
    return this.authService.hasAnyRole(role);
  }

  private handleWarning(message: string, lifetime?: number): void {
    this.handleMessage(message, 'warn', lifetime);
  }

  private handleError(message: string, lifetime?: number): void {
    this.handleMessage(message, 'error', lifetime);
  }

  private handleMessage(message: string, severity?: string, lifetime?: number): void {
    lifetime = lifetime ?? environment.warningElapsedTime;
    severity = severity ?? 'warn';
    this.injector.get(Art57Section).showTooltipMessage({ severity: severity, detail: `${message}`, life: lifetime });
  }
}
